import React, { useEffect, useRef, useState } from 'react';
import DropboxChooser from 'react-dropbox-chooser';
import { FiExternalLink, FiFile, FiFileText, FiFilm, FiImage, FiPaperclip, FiTrash2, FiUploadCloud } from 'react-icons/fi';
import { documentsStatus, formatSize, kindOf } from '../../lib/documents';
import { BUTTON, SIZE } from '../ui/kit';

// Key of the Dropbox Chooser, used only while the service's Dropbox is not linked yet.
const CHOOSER_APP_KEY = '2ejuqgnngalbk6n';

const KIND_ICONS = { pdf: FiFileText, office: FiFileText, sheet: FiFileText, text: FiFileText, image: FiImage, video: FiFilm, audio: FiFilm };
export const DocumentIcon = ({ name, mimeType, size = 14, className = '' }) => {
  const Icon = KIND_ICONS[kindOf(name, mimeType)] || FiFile;
  return <Icon size={size} aria-hidden="true" className={`flex-none ${className}`} />;
};

// { ready: null while asking the server, then true/false, maxSize }
export const useDocumentsStatus = () => {
  const [status, setStatus] = useState({ ready: null, maxSize: 0 });
  useEffect(() => {
    let cancelled = false;
    documentsStatus().then((result) => !cancelled && setStatus(result));
    return () => {
      cancelled = true;
    };
  }, []);
  return status;
};

const ROW = 'flex items-center justify-between gap-2 rounded-xl bg-mist px-[12px] py-2';
const REMOVE = 'flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-full border-0 bg-[transparent] text-red-500 hover:bg-red-50';

// Documents of a form: those already stored (opened in the platform), older Dropbox links,
// and the files chosen on this device, sent when the form is saved.
const AttachmentsField = ({
  id,
  status,
  stored = [],
  onOpen,
  onRemoveStored,
  links = [],
  onLinksChange,
  pending = [],
  onPendingChange,
  sending,
}) => {
  const picker = useRef(null);
  const [refused, setRefused] = useState('');
  const latestLinks = useRef(links);
  latestLinks.current = links;

  const addFiles = (list) => {
    const files = Array.from(list || []);
    const tooLarge = files.filter((file) => status.maxSize && file.size > status.maxSize);
    setRefused(tooLarge.length ? `Trop volumineux (${formatSize(status.maxSize)} au plus) : ${tooLarge.map((file) => file.name).join(', ')}.` : '');
    const accepted = files.filter((file) => !tooLarge.includes(file));
    if (accepted.length) onPendingChange([...pending, ...accepted]);
  };

  return (
    <div className="flex flex-col gap-2">
      {stored.map((item) => (
        <div key={item.id} className={ROW}>
          <button
            type="button"
            onClick={() => onOpen && onOpen(item)}
            className="inline-flex min-w-0 cursor-pointer items-center gap-1.5 border-0 bg-[transparent] p-0 text-left text-[14px] font-medium text-brand-600 hover:text-brand-700"
          >
            <DocumentIcon name={item.name} mimeType={item.mimeType} />
            <span className="break-all">{item.name}</span>
          </button>
          {onRemoveStored && (
            <button type="button" onClick={() => onRemoveStored(item)} aria-label={`Retirer ${item.name}`} className={REMOVE}>
              <FiTrash2 size={14} aria-hidden="true" />
            </button>
          )}
        </div>
      ))}

      {links.map((item, index) => (
        <div key={`${item.link}-${index}`} className={ROW}>
          <a href={item.link} target="_blank" rel="noopener noreferrer" className="inline-flex min-w-0 items-center gap-1.5 break-all text-[14px] font-medium text-brand-600 no-underline hover:text-brand-700">
            <FiExternalLink size={13} aria-hidden="true" className="flex-none" />
            {item.name}
          </a>
          {onLinksChange && (
            <button type="button" onClick={() => onLinksChange(links.filter((_, position) => position !== index))} aria-label={`Retirer ${item.name}`} className={REMOVE}>
              <FiTrash2 size={14} aria-hidden="true" />
            </button>
          )}
        </div>
      ))}

      {pending.map((file, index) => {
        const progress = sending && sending.index === index ? sending.ratio : sending && sending.index > index ? 1 : null;
        return (
          <div key={`${file.name}-${file.size}-${index}`} className={`${ROW} relative overflow-hidden`}>
            {progress !== null && <span className="absolute inset-y-0 left-0 bg-brand-100/70 transition-all" style={{ width: `${Math.round(progress * 100)}%` }} aria-hidden="true" />}
            <span className="relative inline-flex min-w-0 items-center gap-1.5 text-[14px] text-ink">
              <DocumentIcon name={file.name} mimeType={file.type} className="text-black/45" />
              <span className="break-all">{file.name}</span>
              <span className="flex-none text-[12.5px] text-black/40">{progress !== null ? `${Math.round(progress * 100)} %` : formatSize(file.size)}</span>
            </span>
            {!sending && (
              <button type="button" onClick={() => onPendingChange(pending.filter((_, position) => position !== index))} aria-label={`Retirer ${file.name}`} className={`${REMOVE} relative`}>
                <FiTrash2 size={14} aria-hidden="true" />
              </button>
            )}
          </div>
        );
      })}

      {status.ready === true && (
        <>
          <input
            ref={picker}
            id={id}
            type="file"
            multiple
            className="hidden"
            onChange={(event) => {
              addFiles(event.target.files);
              event.target.value = '';
            }}
          />
          <div className="flex flex-wrap items-center gap-x-[12px] gap-y-1">
            <button type="button" onClick={() => picker.current && picker.current.click()} disabled={Boolean(sending)} className={`${BUTTON.secondary} ${SIZE.sm}`}>
              <FiPaperclip size={15} aria-hidden="true" />
              Ajouter des fichiers
            </button>
            <span className="text-[12.5px] text-black/40">Depuis cet appareil{status.maxSize ? ` · ${formatSize(status.maxSize)} au plus par fichier` : ''}</span>
          </div>
        </>
      )}
      {status.ready === false && onLinksChange && (
        <>
          <DropboxChooser
            appKey={CHOOSER_APP_KEY}
            success={(chosen) => onLinksChange([...latestLinks.current, ...chosen.map((item) => ({ link: item.link, name: item.name }))])}
            multiselect
          >
            <button type="button" className={`${BUTTON.secondary} ${SIZE.sm}`}>
              <FiUploadCloud size={15} aria-hidden="true" />
              Choisir dans Dropbox
            </button>
          </DropboxChooser>
          <span className="text-[12.5px] text-black/40">Le Dropbox du service n’est pas encore relié à la plateforme : les documents sont choisis dans votre Dropbox.</span>
        </>
      )}
      {refused && <p className="m-0 text-[12.5px] text-red-600" role="alert">{refused}</p>}
    </div>
  );
};

export default AttachmentsField;
